import { useCallback, useEffect, useState } from "react";

import { ApiError } from "src/core/apiError";
import { Bilge } from "src/gen/rest/bilge/BilgeService";
import {
  GetLinkedAccountsRequest,
  GetLinkedAccountsResponse,
  LinkedAccount,
  UpdateLinkedAccountRequest
} from "src/gen/schema/bilge/api";
import { AdminState } from "src/gen/schema/models/global";

/**
 * Custom hook to manage bot bindings data fetching and state
 */
export const useBindingData = () => {
  const [bindings, setBindings] = useState<LinkedAccount[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<ApiError | undefined>(undefined);

  const fetchBindings = useCallback(() => {
    setLoading(true);
    setError(undefined);
    Bilge.getLinkedAccounts(GetLinkedAccountsRequest.create({}))
      .then((response: GetLinkedAccountsResponse) => {
        setBindings(response.linkedAccounts || []);
      })
      .catch(setError)
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const toggleBindingStatus = useCallback(
    (accountId: string, currentAdminState: number) => {
      const newAdminState =
        currentAdminState === AdminState.ENABLED
          ? AdminState.DISABLED
          : AdminState.ENABLED;
      const request = UpdateLinkedAccountRequest.create({
        accountId: accountId,
        adminState: newAdminState
      });
      setError(undefined);
      setLoading(true);
      Bilge.updateLinkedAccount(request)
        .then(() => {
          return fetchBindings();
        })
        .catch((err: ApiError) => {
          setError(err);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [fetchBindings]
  );

  useEffect(() => {
    fetchBindings();
  }, [fetchBindings]);

  return {
    bindings,
    loading,
    bindingError: error,
    refetchBindings: fetchBindings,
    toggleBindingStatus
  };
};
